// JWT Token Utilities for AWS Cognito

import type { AuthTokens } from '@/types/auth';

/**
 * JWT Token payload interface
 */
interface JWTPayload {
  sub: string;
  email?: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  'cognito:groups'?: string[];
  'cognito:username': string;
  exp: number;
  iat: number;
  token_use: 'id' | 'access';
  aud?: string;
  iss?: string;
}

/**
 * Decode JWT token without verification (for client-side parsing)
 * Note: This is safe for parsing claims but should not be used for verification
 */
export const decodeJWT = (token: string): JWTPayload => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));

    return JSON.parse(decodedPayload) as JWTPayload;
  } catch (error) {
    throw new Error(`Failed to decode JWT: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Check if a JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = decodeJWT(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch {
    // If we can't decode the token, consider it expired
    return true;
  }
};

/**
 * Get token expiration time in milliseconds
 */
export const getTokenExpiration = (token: string): number => {
  try {
    const payload = decodeJWT(token);
    return payload.exp * 1000; // Convert to milliseconds
  } catch {
    return 0;
  }
};

/**
 * Validate token structure and basic claims
 */
export const validateTokenStructure = (token: string): boolean => {
  try {
    const payload = decodeJWT(token);

    // Check required fields
    return !!(
      payload.sub &&
      payload.exp &&
      payload.iat &&
      payload.token_use
    );
  } catch {
    return false;
  }
};

/**
 * Check if tokens are valid and not expired
 */
export const areTokensValid = (tokens: AuthTokens): boolean => {
  try {
    // Check if all required tokens exist
    if (!tokens.accessToken || !tokens.idToken) {
      return false;
    }

    // Check if access token is valid and not expired
    if (!validateTokenStructure(tokens.accessToken) || isTokenExpired(tokens.accessToken)) {
      return false;
    }

    // Check if ID token is valid and not expired
    if (!validateTokenStructure(tokens.idToken) || isTokenExpired(tokens.idToken)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Get time until token expires (in milliseconds)
 */
export const getTimeUntilExpiration = (token: string): number => {
  try {
    const expirationTime = getTokenExpiration(token);
    const currentTime = Date.now();
    return Math.max(0, expirationTime - currentTime);
  } catch {
    return 0;
  }
};

/**
 * Extract auth tokens from URL parameters
 */
export const extractTokensFromUrl = (): AuthTokens | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const idToken = urlParams.get('id_token');
  const accessToken = urlParams.get('access_token');
  const refreshToken = urlParams.get('refresh_token');
  const expiresIn = urlParams.get('expires_in');

  if (!idToken || !accessToken) {
    return null;
  }

  const expiresAt = expiresIn
    ? Date.now() + (parseInt(expiresIn) * 1000)
    : undefined;

  return {
    idToken,
    accessToken,
    refreshToken: refreshToken || undefined,
    expiresAt,
  };
};

/**
 * Clean auth parameters from URL
 */
export const cleanUrlParams = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete('id_token');
  url.searchParams.delete('access_token');
  url.searchParams.delete('refresh_token');
  url.searchParams.delete('expires_in');
  url.searchParams.delete('token_type');
  url.searchParams.delete('state');

  window.history.replaceState({}, document.title, url.toString());
};
